/* eslint-disable no-nested-ternary */
import { useMemo, useState, useCallback } from 'react';
import { capitalCase } from 'change-case';
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import {
  ResetTopic5ContentList,
  selectTopic5ContentPageNumber,
  selectGetTopic5ContentState,
  selectTopic5HasMoreContent,
  selectTopic5ContentList,
  UpdateTopic5HasMoreContent,
  UpdateTopic5PageNumber,
} from '../../../store/slices/contentSlice';
import { getTopic5Content } from '../../../store/thunks/contentThunk';
import ContentHomeSkeleton from './ContentHomeSkeleton';
import { ContentCategoryContainer } from './ContentCategoryContainer';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { useFetchCategoryContent } from './useFetchCategoryContent';
import { Content, ContentFeedSection } from '../../../common/models/content/Content';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import ContentViewMoreSkeleton from './ContentViewMoreSkeleton';

interface Topic5ContentCategoryProps {
  categoryId: string;
  topicName: string;
  viewMorePage?: boolean
  setSomeContentLoadedInitially: (value: boolean) => void
  contentFeedSection: ContentFeedSection
}

export function Topic5ContentCategory({
  categoryId,
  topicName,
  viewMorePage = false,
  setSomeContentLoadedInitially,
  contentFeedSection,
}:Topic5ContentCategoryProps) {
  const storeTopic5ContentList = useAppSelector(selectTopic5ContentList);
  const topic5ContentList = useMemo(() => storeTopic5ContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection,
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [storeTopic5ContentList, contentFeedSection]);

  const getTopic5ContentState = useAppSelector(selectGetTopic5ContentState);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const { t } = useTranslation();
  const topic5Page = useAppSelector(selectTopic5ContentPageNumber);
  const topic5HasMore = useAppSelector(selectTopic5HasMoreContent);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useTrackCategoryContentVisibility({
    categoryContentList: topic5ContentList,
  });

  const fetchMoreCategoryContent = useFetchCategoryContent({
    viewMorePage,
    contentRequestOptions: { contentTopics: topicName },
    getCategoryContent: getTopic5Content,
    resetCategoryContentList: ResetTopic5ContentList,
    setSomeContentLoadedInitially,
    setOpenErrorSnackbar,
    setInitialFetchDone,
    updateCategoryPageNumber: UpdateTopic5PageNumber,
    updateHasMoreCategoryContent: UpdateTopic5HasMoreContent,
  });

  const isLoading = useMemo(
    () => (getTopic5ContentState === 'pending' && !initialFetchDone),
    [getTopic5ContentState, initialFetchDone],
  );

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  const onNextPage = useCallback(() => {
    fetchMoreCategoryContent(
      topic5Page,
      topic5HasMore,
    );
  }, [topic5Page, topic5HasMore, fetchMoreCategoryContent]);

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>

      {isLoading ? (
        viewMorePage ? (
          <ContentViewMoreSkeleton />
        ) : (
          <ContentHomeSkeleton key={`${topicName}-skeleton`} />
        )
      ) : viewMorePage ? (
        <ContentViewMoreContainer
          key={`${categoryId}-content`}
          categoryId={categoryId}
          storeContentList={topic5ContentList}
          onNextPage={onNextPage}
          hasMoreContent={topic5HasMore}
          showSearchBar={true}
        />
      ) : topic5ContentList?.length > 0 ? (
        <ContentCategoryContainer
          categoryId={categoryId}
          key={`${topicName}-content`}
          categoryTitle={capitalCase(topicName)}
          storeContentList={topic5ContentList}
          contentFeedSection={contentFeedSection}
        />
      ) : null}
    </>
  );
}
